<template>
    <!-- Only when a card exists: `awaiting_answer` means there is none. -->
    <div v-if="wig" class="preview">
        <div class="preview__head">
            <h4 class="preview__title">
                {{ $t('pages.assistant.wig.title') }} #{{ wig.id }}
            </h4>
            <a
                class="preview__open"
                :href="`/orders/${wig.id}`"
                target="_blank"
            >
                {{ $t('pages.assistant.wig.open') }}
            </a>
        </div>

        <!-- "(+972…) name surname" — a Latin phone glued to a Hebrew name. -->
        <p v-if="wig.client" class="preview__client" dir="auto">
            {{ wig.client }}
        </p>

        <ul v-if="filled.length" class="preview__list">
            <li
                v-for="{ key, label, value } in filled"
                :key="key"
                class="preview__item"
            >
                <span class="preview__label" dir="auto">{{ label }}</span>
                <span class="preview__value" dir="auto">{{ value }}</span>
            </li>
        </ul>

        <!-- Whatever the assistant could not place in a field goes here in the
             manager's own words. Not leftovers — the part still to be sorted
             out, and the only place it is visible before the card is opened. -->
        <div v-if="wig.details" class="preview__details">
            <span class="preview__details-label">
                {{ $t('pages.assistant.wig.details') }}
            </span>
            <p class="preview__details-text" dir="auto">{{ wig.details }}</p>
        </div>

        <p class="preview__limits">{{ $t('pages.assistant.wig.limits') }}</p>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import languagePropertiesMixin from '@/mixins/language-properties.mixin'
import regularToSnakeCase from '@/helpers/regular-to-snake-case'
import isJson from '@/helpers/isJson'
import { assistantGetterTypes } from '@/store/modules/assistant/types'

// The property pack is keyed on the human label — `wig-type`, `length-from-nape`
// — while the card carries camelCase. `regularToSnakeCase` only splits on spaces
// and underscores, so a camelCase key reaches it whole, lowercases to `wigtype`
// and misses every entry. Splitting the words first is the whole fix: all 27
// properties the assistant can fill are in the pack under that form.
const toPackKey = (value) =>
    regularToSnakeCase(String(value).replace(/([a-z0-9])([A-Z])/g, '$1 $2'))

// Only for a key the pack does not know — a readable label beats raw camelCase
// if the backend ever adds a field.
const humanize = (key) =>
    String(key)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (char) => char.toUpperCase())

export default {
    name: 'AssistantWigPreview',
    mixins: [languagePropertiesMixin],
    computed: {
        ...mapGetters('assistant', {
            wig: assistantGetterTypes.GET_WIG,
        }),
        // v1 WigFullResource — fields that exist only on the v2 resource are
        // not here.
        filled: function () {
            const properties = this.wig?.properties || {}

            return Object.keys(properties)
                .filter((key) => {
                    const value = properties[key]

                    return value !== null && value !== '' && value !== 'none'
                })
                .map((key) => ({
                    key,
                    label: this.fromPack(key) || humanize(key),
                    value: this.readable(properties[key]),
                }))
        },
    },
    methods: {
        fromPack: function (value) {
            return this.propertiesContent[toPackKey(value)]
        },
        // `topType` and `front` arrive as JSON-encoded lists of terms. A value
        // the pack does not carry is shown as it was dictated, not humanized.
        readable: function (value) {
            const parsed = isJson(value) ? JSON.parse(value) : value

            if (Array.isArray(parsed)) {
                return parsed
                    .flat(Infinity)
                    .map((item) => this.fromPack(item) || item)
                    .join(', ')
            }

            return this.fromPack(parsed) || parsed
        },
    },
}
</script>

<style scoped lang="scss">
.preview {
    padding: 12px 16px;
    background: #f7f7f9;
    border-top: 1px solid #eceeef;
    max-height: 40vh;
    overflow-y: auto;

    &__head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
    }

    &__title {
        font-size: 14px;
        font-weight: 600;
        margin: 0;
    }

    &__open {
        font-size: 12px;
        color: #3bafda;
    }

    &__client {
        margin: 2px 0 8px;
        font-size: 13px;
        color: #55595c;
    }

    &__list {
        list-style: none;
        padding: 0;
        margin: 0 0 8px;
    }

    &__item {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 3px 0;
        border-bottom: 1px solid #eceeef;
        font-size: 13px;

        &:last-child {
            border-bottom: none;
        }
    }

    &__label {
        color: #8a8f94;
    }

    &__value {
        text-align: right;

        @include for-hebrew {
            text-align: left;
        }
    }

    &__details {
        margin: 0 0 8px;
        padding: 8px 10px;
        border-radius: 6px;
        background: #fff6e0;
    }

    &__details-label {
        display: block;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #927c3d;
        margin-bottom: 2px;
    }

    &__details-text {
        margin: 0;
        font-size: 13px;
        unicode-bidi: plaintext;
    }

    &__limits {
        margin: 0;
        font-size: 11px;
        color: #8a8f94;
    }
}
</style>
